import { useEffect, useId, useState } from "react";
import type { RecurrenceEditScope } from "../../../../types/api";
import { ConfirmModal } from "../../../../components/ConfirmModal";
import { Radio, RadioGroup } from "../../../../components/Radio";
import { getDeleteScopeCount } from "../../../../services/calendar";
import { deleteCountLabel } from "./recurrenceRule";
import "./Itinerary.css";

/** What the coach is about to do to the series, which changes the copy but not the choices. */
export type RecurrenceScopeAction = "save" | "delete";

interface RecurrenceScopeModalProps {
  isOpen: boolean;
  action: RecurrenceScopeAction;
  /**
   * The occurrence the prompt is about. On a delete it is used to ask the API how many events the
   * selected scope removes. Without it the prompt works as before, just with no count.
   */
  eventId?: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (scope: RecurrenceEditScope) => void;
}

const OPTIONS: { value: RecurrenceEditScope; label: string; hint: string }[] = [
  { value: "This", label: "This event", hint: "Only the occurrence you opened." },
  {
    value: "ThisAndFollowing",
    label: "This and following events",
    hint: "This occurrence and every later one in the series.",
  },
  {
    value: "All",
    label: "All events in the series",
    hint: "Every occurrence, including ones already past.",
  },
];

/** A fetched count, tagged with what it was fetched for so a late answer is never shown for another. */
interface ScopeCount {
  eventId: string;
  scope: RecurrenceEditScope;
  count: number | null;
}

/**
 * "Which occurrences?" prompt shown before saving or deleting one occurrence of a repeating event.
 *
 * Only ever rendered for an event that is part of a series — a standalone event's save and delete
 * must stay exactly as they were, with no extra step. "This" is preselected because it is both the
 * least destructive answer and the server's default, so an accidental confirm changes one week
 * rather than a season.
 *
 * On a delete, the selected option also says how many events will go, because "All" now includes
 * weeks the coach edited on their own. The count is informative only: while it loads, or if the
 * request fails, the option shows no number and Delete still works.
 */
export const RecurrenceScopeModal = ({
  isOpen,
  action,
  eventId,
  busy = false,
  onCancel,
  onConfirm,
}: RecurrenceScopeModalProps) => {
  const groupId = useId();
  const [scope, setScope] = useState<RecurrenceEditScope>("This");
  const [counts, setCounts] = useState<ScopeCount[]>([]);

  // Reset to the safe default each time the prompt opens, so a previous "All" is never inherited
  // by the next, different decision.
  useEffect(() => {
    if (isOpen) setScope("This");
  }, [isOpen]);

  const wantsCount = isOpen && action === "delete" && Boolean(eventId);

  useEffect(() => {
    if (!wantsCount || !eventId) return;
    let active = true;
    const record = (count: number | null) => {
      if (!active) return;
      setCounts((previous) => [
        ...previous.filter((c) => !(c.eventId === eventId && c.scope === scope)),
        { eventId, scope, count },
      ]);
    };
    getDeleteScopeCount(eventId, scope).then(record, () => record(null));
    return () => {
      active = false;
    };
  }, [wantsCount, eventId, scope]);

  const selectedCount = wantsCount
    ? deleteCountLabel(counts.find((c) => c.eventId === eventId && c.scope === scope)?.count)
    : null;

  return (
    <ConfirmModal
      isOpen={isOpen}
      onClose={onCancel}
      onConfirm={() => onConfirm(scope)}
      title={action === "delete" ? "Delete repeating event" : "Edit repeating event"}
      message={
        action === "delete"
          ? "This event repeats. Which occurrences should be deleted?"
          : "This event repeats. Which occurrences should this change apply to?"
      }
      confirmText={action === "delete" ? "Delete" : "Save"}
      cancelText="Cancel"
      busy={busy}
    >
      <RadioGroup
        label="Apply to"
        className="recurrence-scope-group"
        testId="recurrence-scope-options"
      >
        {OPTIONS.map((option) => {
          const countLine = option.value === scope ? selectedCount : null;
          return (
            <Radio
              key={option.value}
              label={option.label}
              hint={
                countLine ? (
                  <>
                    {option.hint}{" "}
                    <span
                      className="recurrence-scope-count"
                      data-testid="recurrence-scope-count"
                    >
                      {countLine}
                    </span>
                  </>
                ) : (
                  option.hint
                )
              }
              name={`${groupId}-scope`}
              value={option.value}
              checked={scope === option.value}
              onChange={() => setScope(option.value)}
              data-testid={`recurrence-scope-${option.value}`}
            />
          );
        })}
      </RadioGroup>
    </ConfirmModal>
  );
};
